<template>
  <router-link
    class="scene-list-card-parts"
    :class="`scene-list-card-parts--role-${card.highlightComment.createCommentUser.userType}`"
    :to="card.link"
  >
    <figure class="scene-list-card-parts__scene-thumbnail">
      <img class="scene-list-card-parts__scene-image" :src="card.highlight.thumbnailPath" alt="" />
      <figcaption
        class="scene-list-card-parts__scene-title"
        v-text="`created by ${card.highlight.createdUserName}`"
      />
    </figure>

    <div class="scene-list-card-parts__content">
      <div class="scene-list-card-parts__content-header">
        <figure class="scene-list-card-parts__user-image">
          <img :src="card.highlightComment.createCommentUser.userPicture" alt="" />
        </figure>

        <scene-card-user-images-parts
          class="scene-list-card-parts__comment-users"
          :otherCommentUsers="card.highlightComment.otherCommentUsers"
          :commentCount="card.highlightComment.commentCount"
        />
      </div>

      <div class="scene-list-card-parts__content-body">
        <p
          class="scene-list-card-parts__user-name"
          v-text="card.highlightComment.createCommentUser.userDisplayName"
        />

        <p class="scene-list-card-parts__comment" v-html="card.highlightComment.comment" />

        <p class="scene-list-card-parts__footer">
          <span class="scene-list-card-parts__footer__lap-pre">Lap</span>
          <span class="scene-list-card-parts__footer__lap-count" v-text="card.highlight.lap" />
          <span class="scene-list-card-parts__footer__lap-sector"
            >-{{ card.highlight.sector }}</span
          >

          <span class="scene-list-card-parts__footer__round">
            {{ card.highlight.round }}
          </span>
        </p>
      </div>
    </div>
  </router-link>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import { v4 as uuidv4 } from 'uuid'
import { orderBy } from 'lodash'
import SceneCardUserImagesParts from '@/components/HomePage/HomePane/parts/SceneCardUserImagesParts.vue'
import { NewArrivalHighlightAdditionalType } from '@/store/stores/pageStore/HomePage/HomePageStore'
import { UserRetrieveNameResponseType } from '@/store/stores/collectionModule/documents/user/UserRetrieveNameDocument'
import HighlightCommentDocument from '@/store/stores/collectionModule/documents/highlight/HighlightCommentDocument'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import type { UserType } from '@/store/stores/pageStore/common/UserType'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import useUserTypeRoles from '@/components/hook/useUserTypeRoles'
import Const from '@/util/Const'
import I18n from '@/locales/I18n'
import ChampionshipDocument from '@/store/stores/collectionModule/documents/championship/ChampionshipDocument'
import RaceDocument from '@/store/stores/collectionModule/documents/race/RaceDocument'

/* eslint-disable @typescript-eslint/no-var-requires */
const defaultIcon = require('@/assets/img/icon/icon_user_default.png')

/**
 * 新着ハイライトカード コメントユーザーのデータ型
 */
export type CreateCommentUserType = {
  id: string
  userDisplayName: string
  userPicture: string
  userType: UserType
}

/**
 * 新着ハイライトカード その他直近コメントユーザーのデータ型
 */
export type OtherCommentUserType = Omit<CreateCommentUserType, 'userType'>

/**
 * 新着ハイライトカード（コメント）のデータ型
 */
type HomeHighlightCommentCardType = {
  highlight: {
    highlightId: string
    thumbnailPath: string
    round: string
    lap: string
    sector: string
    createdUserName: string
  }
  highlightComment: {
    comment: string
    createCommentUser: CreateCommentUserType
    otherCommentUsers?: Array<OtherCommentUserType>
    commentCount: number
  }
  link: string
}

/**
 * HOME ハイライトシーンリスト カードパーツ
 */
export default defineComponent({
  name: 'SceneListCardParts',
  components: {
    SceneCardUserImagesParts,
  },
  props: {
    /**
     * 大会データ
     */
    championships: {
      type: Array as PropType<Array<ChampionshipDocument>>,
      required: true,
    },
    /**
     * レースデータ
     */
    raceList: {
      type: Array as PropType<Array<RaceDocument>>,
      required: true,
    },
    /**
     * ハイライトコメントデータ
     */
    highlightComment: {
      type: Object as PropType<HighlightCommentDocument>,
      required: true,
    },
    /**
     * ハイライト追加データ
     */
    highlightAdditional: {
      type: Object as PropType<NewArrivalHighlightAdditionalType>,
      required: true,
    },
    /**
     * ハイライトIDごとに分類されたハイライトデータ
     */
    highlightsById: {
      type: Object as PropType<Record<string, HighlightDocument>>,
      required: true,
    },
    /**
     * ハイライトIDごとに分類されたハイライトコメントデータ
     */
    commentsByHighlightId: {
      type: Object as PropType<Record<string, Array<HighlightCommentDocument>>>,
      required: true,
    },
    /**
     * 新着ハイライトで利用するユーザー情報
     */
    retrieveNameUsersByUserId: {
      type: Object as PropType<Record<string, UserRetrieveNameResponseType>>,
      required: true,
    },
  },
  setup(props) {
    const { getUserTypeRoles } = useUserTypeRoles()

    /**
     * 新着ハイライトコメントカードのデータ
     */
    const card = computed((): HomeHighlightCommentCardType => {
      const propsHighlightComment = props.highlightComment
      const propsHighlightAdditional = props.highlightAdditional
      const propsUsersByUserId = props.retrieveNameUsersByUserId

      // コメントカードに紐づくハイライト
      const targetHighlight = props.highlightsById?.[propsHighlightComment.highlightId ?? '']
        ? props.highlightsById?.[propsHighlightComment.highlightId ?? '']
        : undefined
      // 対象のレース
      const targetRace = props.raceList.find((race) => race.id === targetHighlight?.matchId)
      // 対象の大会
      const targetChampionship = props.championships.find(
        (championship) => championship.id === targetRace?.championshipMasterId,
      )
      // ハイライトを作成したユーザー
      const createHighlightUser = propsUsersByUserId?.[targetHighlight?._createdBy ?? '']
        ? propsUsersByUserId?.[targetHighlight?._createdBy ?? '']
        : undefined
      // 対象のハイライトコメントカードを作成したユーザー
      const createDisplayCommentCardUser = propsUsersByUserId?.[
        propsHighlightComment._createdBy ?? ''
      ]
        ? propsUsersByUserId?.[propsHighlightComment._createdBy ?? '']
        : undefined

      // ユーザーの種類
      const userTypeRoles = getUserTypeRoles(createDisplayCommentCardUser?.roles)
      const userRole = userTypeRoles?.find((role) => Const.SFGO_ROLES_FOR_USER_TYPE.includes(role))
      const contractPlan = createDisplayCommentCardUser?.contract?.planGroupId.includes('freePlan')
        ? 'free-user'
        : 'paid-user'

      const targetComments = props.commentsByHighlightId?.[targetHighlight?.highlightId ?? '']
        ? props.commentsByHighlightId?.[targetHighlight?.highlightId ?? '']
        : undefined

      // このカードのコメント以外で直近でコメントしたユーザー（3件）
      const otherRecentCommentUsers = orderBy(targetComments, '_createdDate')
        ?.filter((comment) => comment.id !== propsHighlightComment.id)
        .slice(-3)
        .map((filteredComment) => {
          const uuidForOtherComments = uuidv4()
          const targetUser = propsUsersByUserId?.[filteredComment._createdBy ?? '']
            ? propsUsersByUserId?.[filteredComment._createdBy ?? '']
            : undefined

          return {
            id: uuidForOtherComments,
            userDisplayName: targetUser?.additionalData?.userDisplayName ?? '',
            userPicture: targetUser?.userPicture
              ? CloudFrontUtil.getSignedUrl(targetUser?.userPicture)
              : defaultIcon,
          }
        })

      const uuidForCreateComment = uuidv4()
      return {
        highlight: {
          highlightId: targetHighlight?.highlightId ?? '',
          thumbnailPath: targetHighlight?.thumbnailUri
            ? CloudFrontUtil.getSignedUrl(targetHighlight.thumbnailUri)
            : '',
          lap: targetHighlight?.additionalData?.lap || '-',
          sector: targetHighlight?.additionalData?.sector ?? '--',
          round: targetChampionship?.round
            ? `Round${targetChampionship?.round}`
            : targetChampionship?.circuitName[I18n.locale] ?? '',
          // FL-UXで作成したハイライトの場合、SFgo公式を表示する
          createdUserName:
            targetHighlight?._organization === process.env.VUE_APP_SFGO_PARENT_ORG_ID
              ? I18n.tc('HomePage.highlights.card.officialUserName')
              : createHighlightUser?.additionalData?.userDisplayName ?? '',
        },
        highlightComment: {
          comment: propsHighlightComment.comment ?? '',
          createCommentUser: {
            id: uuidForCreateComment,
            userDisplayName: createDisplayCommentCardUser?.additionalData?.userDisplayName ?? '',
            userPicture: createDisplayCommentCardUser?.userPicture
              ? CloudFrontUtil.getSignedUrl(createDisplayCommentCardUser?.userPicture)
              : defaultIcon,
            userType: userRole ?? contractPlan,
          },
          otherCommentUsers: otherRecentCommentUsers,
          commentCount: propsHighlightAdditional.count ?? 0,
        },
        link: targetRace
          ? `/race-video/${targetRace.championshipMasterId}/${targetRace.id}/${targetHighlight?.highlightId}/?playType=highlight&highlightId=${targetHighlight?.highlightId}`
          : '',
      }
    })

    return {
      card,
    }
  },
  methods: {
    /**
     * モーダル表示 通知
     */
    emitClick() {
      this.$emit('click')
    },
  },
})
</script>

<style lang="scss" scoped>
.scene-list-card-parts {
  width: 170px;
  min-width: 170px;
  min-height: 222px;
  overflow: hidden;
  cursor: pointer;
  color: $black;
  border: 1px solid #e5e5e5;
  border-radius: 30px 30px 30px 0;
  background: $white;
  text-align: left;

  &--role {
    &-driver {
      .scene-list-card-parts__content-header {
        background-color: #20cabd;
      }
    }

    &-team-user,
    &-team-engineer {
      .scene-list-card-parts__content-header {
        background-color: #53e2e2;
      }
    }

    &-paid-user {
      .scene-list-card-parts__content-header {
        background-color: #cbaeff;
      }
    }

    &-free-user {
      .scene-list-card-parts__content-header {
        background-color: #e2e0ff;
      }
    }

    &-jrp-staff {
      .scene-list-card-parts__content-header {
        background-color: #4fafff;
      }
    }

    &-guest {
      .scene-list-card-parts__content-header {
        background-color: #ffbb00;
      }
    }

    &-special-supporter,
    &-ambassador,
    &-adviser {
      .scene-list-card-parts__content-header {
        background-color: #ff4949;
      }
    }
  }

  &__scene {
    &-thumbnail {
      position: relative;
      width: calc(100% + 2px);
      height: 70px;
      margin: -1px 0 0 -1px;
      overflow: hidden;
      border-radius: 30px 30px 0 0;
    }

    &-image {
      width: 100%;
      height: 70px;
      object-fit: cover;
    }

    &-title {
      position: absolute;
      left: 0;
      top: 0;
      display: block;
      width: 100%;
      padding: min(#{convertPXtoVW(12)}, 12px) min(#{convertPXtoVW(16)}, 16px);
      font-weight: 500;
      font-size: min(#{convertPXtoVW(10)}, 10px);
      line-height: 1.67;
      letter-spacing: 0.02em;
      font-feature-settings: 'palt' on;
      color: $white;
      background: linear-gradient(180deg, $black 0%, rgba(0, 0, 0, 0) 100%);
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  &__lock {
    position: absolute;
    right: 6px;
    bottom: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    color: $white;
    background-color: rgba(#000, 0.15);
    border-radius: 50%;

    &-icon {
      width: 14px;
      height: 14px;
    }
  }

  &__content {
    position: relative;
    width: 100%;

    &-header {
      padding: 6px 12px;
    }

    &-body {
      padding: 2px 12px 9px;
      background-color: $white;
    }
  }

  &__user {
    &-image {
      margin-top: -26px;

      img {
        width: 50px;
        height: 50px;
        overflow: hidden;
        border-radius: 50%;
        background: $white;
        object-fit: cover;
      }
    }

    &-name {
      width: 100%;
      margin-bottom: 3px;
      font-weight: 700;
      font-size: 13px;
      line-height: 1.58;
      letter-spacing: 0.02em;
      font-feature-settings: 'palt' on;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  &__comment-users {
    position: absolute;
    right: 12px;
    top: 6px;
  }

  &__premium-only {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
    height: 60px;
    margin-bottom: 3px;
    font-family: $fontHeading;
    font-weight: 600;
    font-size: 11px;
    line-height: 1.64;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.2);
  }

  &__comment {
    width: 100%;
    height: 60px;
    margin-bottom: 3px;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
    display: -webkit-box;
    overflow: hidden;

    /* autoprefixer: off */
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  /*
    以下のエラーがでるため、ブロックを分けて記載する。
    `Second Autoprefixer control comment was ignored. Autoprefixer applies control comment to whole block, not to next rules.`
  */

  /* autoprefixer: on */

  &__footer {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-weight: 600;
    font-size: 11px;
    line-height: 1.09;
    letter-spacing: 0.04em;

    &__lap {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: flex-start;

      &-pre {
        font-size: 11px;
        line-height: 1.09;
      }

      &-count {
        font-size: 16px;
        line-height: 1.125;
      }

      &-sector {
        font-size: 11px;
        line-height: 1.09;
        transform: translateY(0.5px);
      }
    }

    &__round {
      width: 63px;
      text-align: right;
      border-left: 1px solid rgba(0, 0, 0, 0.2);
      transform: translateY(0.5px);
      padding-left: 6px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
}
</style>
